import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import { EdwardColors } from 'enums';
import '../../../components/AppTheme/index';

interface DetailProps {
  userMode?: boolean;
  editMode?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: (props: DetailProps) => ({
      '& .DETAIL_HEADER': {
        background: '#fcfcfc',
        padding: theme.spacing(2, 4, 4, 4),
        boxShadow: '0 4px 4px rgba(0,0,0,0.08)',
        [theme.breakpoints.down('xs')]: {
          padding: theme.spacing(2),
        },
      },
      '& .DETAIL_ACTIONS': {
        display: props.userMode ? 'none' : undefined,
        flexBasis: 0,
        flexGrow: 1,
        '&.EDIT': {
          flexBasis: 'auto',
          order: 1,
          [theme.breakpoints.down('xs')]: {
            flexBasis: 'content',
          },
        },
        [theme.breakpoints.down('xs')]: {
          flexBasis: 'content',
        },
      },

      '& .DETAIL_MENU': { display: 'none' },
      [theme.breakpoints.down('xs')]: {
        '& .DETAIL_MENU': {
          order: 3,
          minWidth: '48px',
          display: props.userMode ? 'none' : 'flex',
        },
        '& .DETAIL_ACTIONS': { order: 2 },
      },
      '& .DETAIL_CONTENT': {
        padding: theme.spacing(2, 4),
        [theme.breakpoints.down('xs')]: {
          padding: theme.spacing(0, 1, 2, 1),
        },
      },
    }),
    notifications: {
      '& > div:not(.MuiContainer-maxWidthMd)': {
        marginLeft: theme.spacing(4),
        marginRight: theme.spacing(4),
        width: `calc(100% - ${theme.spacing(8)}px)`,
      },
      '& > div:first-child': {
        marginTop: theme.spacing(4),
      },
      width: '100%',
      [theme.breakpoints.down('xs')]: {
        '& > div:not(.MuiContainer-maxWidthMd)': {
          marginLeft: theme.spacing(2),
          marginRight: theme.spacing(2),
          width: `calc(100% - ${theme.spacing(4)}px)`,
        },
        '& > div:first-child': {
          marginTop: theme.spacing(2),
        },
        '& > div:last-child': { marginBottom: 0 },
      },
    },
    actionsWrapper: (props: DetailProps) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      marginBottom: theme.spacing(2),

      [theme.breakpoints.down('xs')]: {
        flexWrap: props.userMode ? 'nowrap' : 'wrap',
        marginBottom: 0,
      },
    }),
    title: (props: DetailProps) => ({
      fontSize: '1.5rem',
      display: 'flex',
      flexBasis: props.userMode ? '100%' : 'content',
      flexGrow: props.userMode ? 0 : 1,
      '&.EDIT': {
        order: 2,
        [theme.breakpoints.down('xs')]: {
          order: 3,
          flexBasis: '100%',
          width: '100%',
        },
      },
      '& h2': {
        margin: 0,
        fontSize: '28px',
        fontWeight: 900,
        padding: theme.spacing(0, 0, 0, 1),
      },
      [theme.breakpoints.down('xs')]: {
        order: 1,
        '& h2': {
          marginRight: props.userMode ? 0 : theme.spacing(1),
        },
        '& h2 > span': { display: 'inline-flex' },
      },
    }),
    infoBox: {
      paddingLeft: theme.spacing(2),
      alignContent: 'flex-start',
      '@media (max-width: 1450px)': {
        '& span, & em': { display: 'block' },
      },
      [theme.breakpoints.down('xs')]: {
        padding: '4px 0 4px 12px',
        '& span, & em': { display: 'inline !important' },
      },
    },
    hideBoxSm: {
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    value: {
      maxWidth: '100%',
      wordWrap: 'break-word',
      fontWeight: 800,
      paddingTop: theme.spacing(1),
      '& a': {
        maxWidth: '100%',
        display: 'inline-flex',
        color: '#000',
        flexWrap: 'wrap',
      },
      '& a span': {
        display: 'inline-flex',
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0, 0, 0, 1),
      },
      [theme.breakpoints.up('xl')]: {
        padding: theme.spacing(0, 0, 0, 1),
      },
    },
    label: {
      '@media (max-width: 820px)': {
        minHeight: '2.5rem',
      },
      [theme.breakpoints.down('xs')]: {
        minHeight: 0,
      },
    },
    bigValue: {
      fontSize: '28px',
      fontWeight: 900,
      '& em': { fontSize: '1rem', fontStyle: 'normal' },
      paddingTop: theme.spacing(2),
      alignSelf: 'flex-start',
      [theme.breakpoints.down('md')]: {
        fontSize: '19px',
        fontWeight: 600,
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(0, 0, 1, 0),
        fontSize: '28px',
        fontWeight: 900,
      },
      '& span.positive': {
        color: EdwardColors.POSITIVE,
      },
      '& span.negative': {
        color: EdwardColors.NEGATIVE,
      },
    },
    balance: {
      color: EdwardColors.SECONDARY,
    },
    actionButton: { width: '100%' },
    accountSelection: {
      margin: '5px 0 5px 0',
      '& .MuiSelect-root': { fontSize: '20px', lineHeight: '23px' },
    },
    clientHeader: {
      position: 'relative',
      backgroundColor: EdwardColors.LIGHTER,
      borderRadius: theme.borderRadius.box,
      minHeight: '60px',
      padding: theme.spacing(1, 0),
      fontSize: '18px',
      '& .MuiTypography-h1': {
        marginBottom: 0,
        paddingLeft: theme.spacing(2),
        fontSize: '28px',
        fontWeight: 900,
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '16px',
        '& .MuiTypography-h1': { fontSize: '24px', paddingRight: '48px' },
      },
    },
    collapsible: {
      display: 'flex',
      maxWidth: '75%',
      flexBasis: '75%',
      '& > div': { width: '100%' },
      [theme.breakpoints.down('md')]: {
        maxWidth: '100%',
        flexBasis: '100%',
      },
    },
    // TODO: remove after collapsible control comp.
    expandAction: {
      padding: `0 !important`,
      marginRight: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
      '& svg': {
        width: '1.8em',
        height: '1.8em',
        transition: `transform .${theme.transitions.duration.short}s`,
        color: EdwardColors.SECONDARY,
      },
    },
    expandActionExpanded: {
      '& svg': {
        transform: 'rotate(180deg)',
      },
    },
    profileInfo: {
      color: '#000',
      fontSize: '14px',
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },

    summary: {
      background: '#fcfcfc',
      padding: theme.spacing(2, 0),
      marginBottom: theme.spacing(2),
      borderRadius: theme.borderRadius.box,
      fontSize: '18px',
      alignItems: 'stretch',
      [theme.breakpoints.down('md')]: {
        fontWeight: 'normal',
        fontSize: '14px',
      },
      [theme.breakpoints.down('xs')]: {
        marginBottom: theme.spacing(2),
      },
    },

    detailHeader: { padding: theme.spacing(2) },

    newButtonContainer: {
      textAlign: 'right',
      '& > *': {
        margin: theme.spacing(2, 1),
      },
      [theme.breakpoints.down('sm')]: {
        '& > *': {
          margin: theme.spacing(0, 2, 1, 0),
        },
      },
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
    clientName: {
      '& > span:first-child': { marginLeft: theme.spacing(2) },
      '& button': { padding: '8px' },
    },
    RPIcon: {
      display: 'inline-block',
      width: '24px',
      height: '24px',
      position: 'relative',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '24px',
      color: EdwardColors.LIGHTER,
      '&.rp10': {
        letterSpacing: '-1px',
        '& > span': { left: '-1px' },
      },
      textAlign: 'center',
      '& svg, & span': {
        position: 'absolute',
        left: 0,
        top: 0,
      },
      '& span': {
        display: 'inline-block',
        width: '100%',
        height: '100%',
      },
    },
    tabs: {
      position: 'relative',
      boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
      '& > header .MuiContainer-root.MuiContainer-maxWidthMd': {
        marginLeft: 0,
        paddingRight: theme.spacing(5),
      },
      [theme.breakpoints.down('sm')]: {
        '& .MuiTab-root': { fontSize: '12px' },
        '& .MuiAppBar-root': { marginBottom: theme.spacing(1) },
      },
      [theme.breakpoints.down('xs')]: {
        '& .MuiAppBar-root': { marginBottom: theme.spacing(4) },
      },
      '@media (max-width:420px)': {
        '& .MuiTab-root': { fontSize: '10px', padding: '6px 0' },
      },
      '@media (max-width:350px)': {
        '& .MuiTab-root': { fontSize: '9px' },
      },
    },
    actionButtons: {
      textAlign: 'right',
      [theme.breakpoints.down('xs')]: {
        textAlign: 'left',
      },
    },
    buttonWithNote: {
      '& span > svg': {
        position: 'absolute',
        left: '-12px',
        top: '-8px',
        color: EdwardColors.ALERT,
      },
      '&.MuiTab-labelIcon': {
        minHeight: 'auto',
        paddingTop: 0,
      },
      '&.MuiTab-labelIcon > span.MuiTab-wrapper > svg:first-child': {
        margin: theme.spacing(0, 0, 0, 2),
      },
    },
    downloadButton: {
      '@media (max-width:400px)': {
        height: '32px',
        minWidth: '32px',
        width: '32px',
        '& .MuiButton-startIcon': {
          margin: 0,
          '& .MuiSvgIcon-root': {
            width: '1.5em',
            height: '1.5em',
          },
        },
      },
    },
    accounts: {
      display: 'inline-flex',
      alignItems: 'center',
      height: theme.spacing(4),
      lineHeight: theme.spacing(4),
      cursor: 'pointer',
      paddingBottom: theme.spacing(2),
      '&:hover': {
        color: EdwardColors.SECONDARY,
      },
      '& svg': {
        fontSize: '1.2em',
      },
    },
  }),
);

export default useStyles;
